import { useEffect, useRef, useState, useCallback } from "react";
import {
  getZonasEnvio,
  crearZonaEnvio,
  actualizarZonaEnvio,
  obtenerZonaEnvioPorId,
} from "../../../services/usuarios_tienda/zonas_envio";
import ShippingZoneFormModal from "./ShippingZoneFormModal";
import { IconEdit } from "../../../icons/Icons";
import { hasPerm } from "../../../utils/hasPerm";
import "./ShippingZonesPage.css";

function ShippingZonesPage() {
  const [zonas, setZonas] = useState([]);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const [loading, setLoading] = useState(false);
  const [editLoadingId, setEditLoadingId] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [selectedZona, setSelectedZona] = useState(null);

  const [alert, setAlert] = useState(null);
  const alertTimerRef = useRef(null);

  const showAlert = useCallback((type, message) => {
    setAlert({ type, message: String(message || "") });

    if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    alertTimerRef.current = window.setTimeout(() => setAlert(null), 3500);
  }, []);

  useEffect(() => {
    return () => {
      if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    };
  }, []);

  const cargarZonas = useCallback(
    async (currentPage = 1) => {
      try {
        setLoading(true);

        const response = await getZonasEnvio({
          page: currentPage,
          limit: pageSize,
        });

        setZonas(Array.isArray(response?.data) ? response.data : []);
        setPagination(
          response?.pagination || {
            page: currentPage,
            limit: pageSize,
            total: 0,
            totalPages: 1,
            hasNextPage: false,
            hasPreviousPage: false,
          }
        );
      } catch (error) {
        console.error("Error cargando zonas de envío:", error);
        showAlert(
          "error",
          error?.response?.data?.message ||
            error?.message ||
            "No se pudieron cargar las zonas de envío."
        );
        setZonas([]);
        setPagination({
          page: currentPage,
          limit: pageSize,
          total: 0,
          totalPages: 1,
          hasNextPage: false,
          hasPreviousPage: false,
        });
      } finally {
        setLoading(false);
      }
    },
    [pageSize, showAlert]
  );

  useEffect(() => {
    void cargarZonas(page);
  }, [page, cargarZonas]);

  const openCreateModal = () => {
    setSelectedZona(null);
    setShowModal(true);
  };

  const openEditModal = async (zona) => {
    const zonaId = Number(zona?.tienda_zona_envio_id);

    if (!Number.isInteger(zonaId) || zonaId <= 0) {
      showAlert("error", "No se pudo identificar la zona a editar.");
      return;
    }

    try {
      setEditLoadingId(zonaId);

      const response = await obtenerZonaEnvioPorId(zonaId);

      setSelectedZona(response?.data || null);
      setShowModal(true);
    } catch (error) {
      console.error("Error obteniendo zona de envío por id:", error);
      showAlert(
        "error",
        error?.response?.data?.message ||
          error?.message ||
          "No se pudo cargar la zona de envío."
      );
    } finally {
      setEditLoadingId(null);
    }
  };

  const handleCreate = async (data) => {
    try {
      const res = await crearZonaEnvio(data);

      showAlert("success", res?.message || "Zona de envío creada correctamente.");
      setShowModal(false);
      setSelectedZona(null);
      setPage(1);
      await cargarZonas(1);

      return res;
    } catch (error) {
      console.error("Create shipping zone failed:", error);
      throw error;
    }
  };

  const handleUpdate = async (id, data) => {
    try {
      const res = await actualizarZonaEnvio(id, data);

      showAlert("success", res?.message || "Zona de envío actualizada correctamente.");
      setShowModal(false);
      setSelectedZona(null);
      await cargarZonas(page);

      return res;
    } catch (error) {
      console.error("Update shipping zone failed:", error);
      throw error;
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedZona(null);
  };

  const nextPage = () => {
    if (pagination.hasNextPage) setPage((prev) => prev + 1);
  };

  const prevPage = () => {
    if (pagination.hasPreviousPage) setPage((prev) => prev - 1);
  };

  const goToPage = (n) => {
    const total = pagination.totalPages || 1;
    if (n < 1 || n > total || n === pagination.page || loading) return;
    setPage(n);
  };

  const visiblePages = (() => {
    const total = pagination.totalPages || 1;
    const current = pagination.page || 1;
    const count = 3;
    if (total <= count) return Array.from({ length: total }, (_, i) => i + 1);
    let s = Math.max(1, current - Math.floor(count / 2));
    let e = s + count - 1;
    if (e > total) {
      e = total;
      s = e - count + 1;
    }
    return Array.from({ length: e - s + 1 }, (_, i) => s + i);
  })();

  const formatDate = (value) => {
    if (!value) return "-";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleDateString("es-GT", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  return (
    <div className="tienda-page-scope">
      {alert && (
        <div className={`omx-alert omx-alert--${alert.type}`}>
          <span className="omx-alert__msg">{alert.message}</span>
          <button className="omx-alert__x" onClick={() => setAlert(null)}>
            ✕
          </button>
        </div>
      )}

      <div className="header">
        <div className="methods-header__row">
          <h2 className="title">Zonas de envío</h2>

          {hasPerm("create_shipping_methods") && (
            <button type="button" className="methods-btn-create" onClick={openCreateModal}>
              Crear zona
            </button>
          )}
        </div>

        <p className="page-lead">
          Agrupa ubicaciones en zonas para asignarles reglas y costos de envío.
        </p>
      </div>

      <div className="table-card">
        <div className="table-card-scroll">
        <table className="modern-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Código</th>
              <th>Nombre</th>
              <th>Descripción</th>
              <th>Ubicaciones</th>
              <th>Fecha creado</th>
              <th style={{ textAlign: "right" }}>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="no-data">
                  Cargando zonas de envío...
                </td>
              </tr>
            ) : zonas.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">
                  No se encontraron zonas de envío
                </td>
              </tr>
            ) : (
              zonas.map((z) => {
                const rowEditLoading =
                  editLoadingId !== null &&
                  Number(editLoadingId) === Number(z.tienda_zona_envio_id);

                return (
                  <tr key={z.tienda_zona_envio_id}>
                    <td>{z.tienda_zona_envio_id}</td>
                    <td>{z.codigo}</td>
                    <td>{z.nombre}</td>
                    <td>{z.descripcion || "-"}</td>
                    <td>{Number(z.total_detalles || 0)}</td>
                    <td>{formatDate(z.fecha_creado)}</td>
                    <td style={{ textAlign: "right" }}>
                        {hasPerm("update_shipping_methods") && (
                          <span
                            className="action-icon edit"
                            onClick={() => !rowEditLoading && openEditModal(z)}
                            title="Editar zona de envío"
                            style={
                              rowEditLoading
                                ? { opacity: 0.65, pointerEvents: "none" }
                                : undefined
                            }
                          >
                            {rowEditLoading ? "..." : IconEdit}
                          </span>
                        )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>
      </div>

      {showModal && (
        <ShippingZoneFormModal
          isOpen={showModal}
          zonaData={selectedZona}
          onClose={handleCloseModal}
          onSubmit={(data) =>
            selectedZona
              ? handleUpdate(selectedZona.tienda_zona_envio_id, data)
              : handleCreate(data)
          }
        />
      )}

      {(pagination.total > 0 || zonas.length > 0) && (
        <div className="pagination">
          <div className="pagination__center">
            <div className="pagination__nav" aria-label="Paginación de zonas de envío">
              <button
                type="button"
                className="pagination__arrow"
                onClick={prevPage}
                disabled={!pagination.hasPreviousPage || loading}
                aria-label="Página anterior"
              >
                ‹
              </button>

              <div className="pagination__pages">
                {visiblePages.map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`pagination__page${n === pagination.page ? " is-active" : ""}`}
                    onClick={() => goToPage(n)}
                    aria-label={`Ir a página ${n}`}
                    aria-current={n === pagination.page ? "page" : undefined}
                  >
                    {n}
                  </button>
                ))}
              </div>

              <button
                type="button"
                className="pagination__arrow"
                onClick={nextPage}
                disabled={!pagination.hasNextPage || loading}
                aria-label="Página siguiente"
              >
                ›
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ShippingZonesPage;